/** The battle screen: the Pixi arena, its console, and the spellbook over them.
 *
 * Everything on `BattleView.ui` - the active stack, the hexes it may reach, the
 * hexes it may strike from, the hexes a spell may land on - is computed here,
 * from the kernel, and handed to the renderer. The renderer must not derive
 * them (I10), and this is the file that keeps that promise honest.
 *
 * Two things about the shape of this file are load-bearing, because getting
 * either wrong is what made the board inert:
 *
 * 1. `BattleState` is mutated in place by `executeBattleCommand`, so the object
 *    identity does not change when the battle does. `props.version` is the only
 *    honest change signal, and every memo that reads `state` names it.
 * 2. The presenter exists only once `mount()` resolves, which is after the
 *    render that created it. The pointer handlers are therefore attached on a
 *    `mountedAt` bump, exactly as `WorldCanvas` does, and they read what they
 *    need from a ref rather than from a closure, so nothing has to re-attach
 *    them when the state moves.
 */

import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type {
  BattleCommand, BattleState, ContentRegistry, GameEvent, HexCoord, SpellDef, SpellDefId,
} from "@game/core";
import {
  SIDE_ATTACKER, activeStack, attackPositions, computeReach,
  enumerateLegalBattleCommands, magicProfile, newOccupancy, spellManaCost,
  tacticsSide,
} from "@game/core";
import type { AtlasLike, BattleAction, CommanderView, UiState } from "@game/render";
import {
  ARENA_W, BATTLE_ACTIONS, BattlePresenter, CONSOLE_H, arenaScale, arenaViewRect,
  attackApproach, asHex, consoleLayout,
} from "@game/render";
import {
  BattleInfoPanel, battleHexCard, battleStackCard, usePhoneLayout, useHotkeys,
} from "@game/ui";
import type { InfoCard } from "@game/ui";
import { app } from "../game/app.js";
import { consoleIntent, tacticsClick, tacticsDone } from "./commands.js";
import { narrateBattleEvents, narrationWindow } from "./narrate.js";

const TOUCH_CONSOLE_LABEL: Readonly<Record<BattleAction, string>> = Object.freeze({
  wait: "Wait",
  defend: "Defend",
  spellbook: "Spells",
  retreat: "Retreat",
  surrender: "Surrender",
  strike: "Strike",
  racial: "Racial",
  activate: "Ability",
});

export interface BattleScreenProps {
  readonly atlas: AtlasLike;
  readonly content: ContentRegistry;
  readonly state: BattleState;
  /** Version to redraw on; the session's counter. */
  readonly version: number;
  /** Events since the last frame drawn. Played, then cleared by the host. */
  readonly events: readonly GameEvent[];
  /** The side the viewer controls, or null when spectating. */
  readonly side: 0 | 1 | null;
  /**
   * Who is actually commanding each army, attacker first.
   *
   * `BattleHeroState` carries a `HeroId` and no def id, so neither this screen
   * nor the renderer can name its own commander; the host holds the `GameState`
   * the id indexes and is the only party that can. Left undefined the console
   * falls back to a **stand-in** drawn from the army's faction roster, which is
   * faction-correct, class-correct and the wrong person - so a host that has a
   * `GameState` must pass this.
   */
  readonly commanders?: readonly [CommanderView | null, CommanderView | null] | undefined;
  readonly onCommand: (cmd: BattleCommand) => void;
  readonly animations: boolean;
  readonly presenterRef?: { current: BattlePresenter | null } | undefined;
}

type Cast = Extract<BattleCommand, { type: "BATTLE_CAST" }>;

const EMPTY: readonly number[] = Object.freeze([]);
const NO_COMMANDS: readonly BattleCommand[] = Object.freeze([]);

/** Hexes the active stack can put its head on, from the kernel's own reach. */
function reachOf(state: BattleState, slot: number): readonly number[] {
  if (slot < 0) return EMPTY;
  const reach = computeReach(state, slot, newOccupancy(state));
  const out: number[] = [];
  for (let h = 0; h < reach.dist.length; h++) if (reach.dist[h]! >= 0) out.push(h);
  return out;
}

/** Which stack, if any, has a body on this hex. The kernel builds the map, so a
 *  two-hex stack's tail counts exactly as the kernel counts it. */
function occupantAt(state: BattleState, hex: number): number | null {
  const occ = newOccupancy(state);
  const slot = occ[hex] ?? -1;
  return slot < 0 ? null : slot;
}

/** Every hex a stack stands on - head and tail - so a spell aimed at a stack can
 *  be clicked anywhere on it. */
function bodyOf(state: BattleState, slot: number): readonly number[] {
  const occ = newOccupancy(state);
  const out: number[] = [];
  for (let h = 0; h < occ.length; h++) if (occ[h] === slot) out.push(h);
  return out;
}

/** A spell armed from the book, and where its next click may land.
 *
 *  The candidate list is the kernel's own enumeration, so a spell offered here
 *  executes `Ok` by construction (Section 3.3) and this file decides no rule.
 *  What it does decide is presentation: whether the enumerator's `target` is a
 *  hex or a stack is a property of `SpellDef.targeting`, and a stack slot and a
 *  hex index are both small integers, so the two must never be compared blind. */
interface Aim {
  readonly def: SpellDef;
  /** Hexes the player may click, and which cast each one issues. */
  readonly byHex: ReadonlyMap<number, Cast>;
  readonly hexes: readonly number[];
}

export function BattleScreen(props: BattleScreenProps): JSX.Element {
  const { atlas, content, state, side, version } = props;
  const phone = usePhoneLayout();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const localRef = useRef<BattlePresenter | null>(null);
  // The presenter the instant it is constructed, before `mount()` resolves.
  // `localRef` deliberately stays null until there is a live canvas behind it;
  // this one exists so the pointer can be claimed on the first render and the
  // presenter can bind it when the canvas appears.
  const builtRef = useRef<BattlePresenter | null>(null);
  const [mountedAt, setMountedAt] = useState(0);
  const [hover, setHover] = useState<{ hex: number | null; stack: number | null; x: number; y: number }>(
    { hex: null, stack: null, x: 0, y: 0 },
  );
  const [bookOpen, setBookOpen] = useState(false);
  /**
   * The stack whose figures the info panel shows: set by a right-click on it,
   * held while the pointer stays on it, cleared the moment it leaves. The classic
   * shows a stack's numbers on right-click only; hovering an enemy used to pop
   * the whole card, which a critic named as not the game's behaviour.
   */
  const [inspect, setInspect] = useState<number | null>(null);
  /**
   * Info mode: the board becomes a reference sheet.
   *
   * A ref beside the state because the pointer handlers are attached once and
   * read through `live`/refs - a handler closing over the state variable would
   * still see `false` on the press after the toggle.
   */
  const [infoMode, setInfoMode] = useState(false);
  const infoModeRef = useRef(false);
  infoModeRef.current = infoMode;
  const [infoCard, setInfoCard] = useState<{ card: InfoCard; x: number; y: number } | null>(null);
  /** Where the last press landed in VIEWPORT pixels; `point` is arena space. */
  const pressRef = useRef({ x: 0, y: 0 });
  const [aiming, setAiming] = useState<SpellDefId | null>(null);
  /**
   * The hero's blow is armed and waiting for a target.
   *
   * Its own state rather than a sentinel inside `aiming`, which holds a spell
   * id: a union of "a spell" and "not a spell" in one slot would have every
   * read of it ask which it was, and the two disarm at the same moments anyway.
   */
  const [striking, setStriking] = useState(false);
  /**
   * A spell the ACTIVE STACK is about to cast, armed and waiting for a target.
   *
   * Its own state beside `aiming` rather than a flag inside it, for the reason
   * `striking` has its own: the two hold the same kind of value but answer to
   * different commands, and a union in one slot would make every read ask
   * which it was. They disarm together, which is the only thing they share.
   *
   * Without this the whole creature-casting layer was unreachable: the kernel
   * enumerated `BATTLE_CREATURE_CAST`, the AI scored it, and no button in this
   * app could issue one - so a Wraith's Sorrow existed and could not be cast.
   */
  const [creatureSpell, setCreatureSpell] = useState<SpellDefId | null>(null);
  /** The creature's spell list, opened when it is too long to lay out flat. */
  const [castMenu, setCastMenu] = useState(false);
  /**
   * The console's running commentary, oldest line first.
   *
   * Held rather than recomputed because a `GameEvent` batch is seen exactly
   * once - the host clears `props.events` after the frame - and the sentences
   * it produced have to stay on the strip until the next action pushes them
   * off. Two lines, which is what the recess holds; see `narrate.ts`.
   */
  const [narration, setNarration] = useState<readonly string[]>([]);
  /** The stage in CSS pixels, so the spellbook can find its own button. */
  const [stage, setStage] = useState<{ readonly w: number; readonly h: number }>({ w: 0, h: 0 });

  /** Quick Combat's state lives on the app model; the chip only reflects it. */
  const autoBattle = useSyncExternalStore(
    useCallback((l: () => void) => app.subscribe(l), []),
    useCallback(() => app.getSnapshot().autoBattle, []),
  );
  // Declared as data so the binding and the chip cannot disagree about what
  // the key does, and so the help overlay can list it.
  useHotkeys(useMemo(() => (side === null ? [] : [{
    combo: "a",
    description: autoBattle ? "Take command back from the computer" : "Let the computer fight this battle",
    onFire: () => { app.setAutoBattle(!autoBattle); },
  }]), [side, autoBattle]));

  const slot = activeStack(state);
  const placing = state.tacticsPhase && side !== null && side === tacticsSide(state);
  const mine = !state.tacticsPhase && slot >= 0 && side !== null && state.stacks.side[slot] === side;
  const [picked, setPicked] = useState(-1);

  // One enumeration per frame, shared by the click handler, the spellbook and
  // the console. Asking the kernel twice for the same answer is how the two
  // drift apart.
  const legal = useMemo(
    () => (side === null ? NO_COMMANDS : enumerateLegalBattleCommands(state, side, content)),
    [state, version, side, content],
  );

  const casts = useMemo(
    () => legal.filter((c): c is Cast => c.type === "BATTLE_CAST"),
    [legal],
  );

  /**
   * The active stack's own abilities, read off the legal list.
   *
   * These are creature powers rather than the four things every stack can do,
   * so they cannot live on the arena's console: its stone buttons are a fixed
   * set in the renderer and only one stack in a battle will usually have any of
   * these at all. Same reasoning as the spellbook two blocks down, and the same
   * arrangement - a DOM strip over the canvas.
   *
   * Discovered from `enumerateLegalBattleCommands` rather than by asking the
   * content whether this creature has the ability. That is the difference
   * between offering a command and mirroring a rule: the kernel decides what is
   * legal, and a power that stops being enumerable stops being offered without
   * this file changing. It is also why an ability the kernel implements but
   * never enumerates cannot appear here at all - see the BATTLE_MEDITATE entry
   * in `packages/ui/test/reachability.spec.ts`.
   */
  const abilities = useMemo(
    () => (mine ? legal.filter((c) => c.type in ABILITY_LABEL && "stack" in c
      && (c as { stack: number }).stack === slot) : []),
    [legal, mine, slot],
  );

  /** One entry per spell the hero may cast right now, priced at his own mastery. */
  const spellChoices = useMemo((): readonly { readonly def: SpellDef; readonly mana: number }[] => {
    if (casts.length === 0) return [];
    const seen: string[] = [];
    const out: { def: SpellDef; mana: number }[] = [];
    for (const c of casts) {
      if (seen.includes(c.spell as string)) continue;
      seen.push(c.spell as string);
      const def = content.spells.get(c.spell);
      if (def === undefined) continue;
      out.push({ def, mana: spellManaCost(def, magicProfile(state, side ?? SIDE_ATTACKER, def)) });
    }
    out.sort((a, b) => a.def.level - b.def.level || (a.def.name < b.def.name ? -1 : 1));
    return out;
  }, [casts, content, state, version, side]);

  /** Every creature cast the ACTIVE stack may make, off the kernel's own list. */
  const creatureCasts = useMemo(
    () => (mine
      ? legal.filter((c): c is Extract<BattleCommand, { type: "BATTLE_CREATURE_CAST" }> =>
        c.type === "BATTLE_CREATURE_CAST" && c.stack === slot)
      : []),
    [legal, mine, slot],
  );

  /** One button per distinct spell it knows, named for the strip. */
  const creatureChoices = useMemo((): readonly SpellDef[] => {
    const seen: string[] = [];
    const out: SpellDef[] = [];
    for (const c of creatureCasts) {
      if (seen.includes(c.spell as string)) continue;
      seen.push(c.spell as string);
      const def = content.spells.get(c.spell);
      if (def !== undefined) out.push(def);
    }
    out.sort((a, b) => (a.name < b.name ? -1 : 1));
    return out;
  }, [creatureCasts, content]);

  /** The armed creature spell's landing hexes, the shape `aim` has. */
  const creatureAim = useMemo((): Aim | null => {
    if (creatureSpell === null) return null;
    const list = creatureCasts.filter((c) => c.spell === creatureSpell);
    const def = content.spells.get(creatureSpell);
    if (list.length === 0 || def === undefined) return null;
    const byHex = new Map<number, BattleCommand>();
    const hexes: number[] = [];
    const put = (h: number, c: BattleCommand): void => {
      if (byHex.has(h)) return;
      byHex.set(h, c);
      hexes.push(h);
    };
    if (def.targeting === "AREA" || def.targeting === "HEX") {
      for (const c of list) put(c.target, c);
    } else {
      for (const c of list) for (const h of bodyOf(state, c.target)) put(h, c);
    }
    return { def, byHex, hexes } as Aim;
  }, [creatureSpell, creatureCasts, content, state, version]);

  /** Enemy slots the hero may strike right now, off the kernel's own list. */
  const strikeTargets = useMemo(
    () => legal.filter((c): c is Extract<BattleCommand, { type: "BATTLE_HERO_STRIKE" }> =>
      c.type === "BATTLE_HERO_STRIKE"),
    [legal],
  );

  const aim = useMemo((): Aim | null => {
    if (aiming === null) return null;
    const list = casts.filter((c) => c.spell === aiming);
    const def = content.spells.get(aiming);
    if (list.length === 0 || def === undefined) return null;
    const byHex = new Map<number, Cast>();
    const hexes: number[] = [];
    const put = (h: number, c: Cast): void => {
      if (byHex.has(h)) return;
      byHex.set(h, c);
      hexes.push(h);
    };
    if (def.targeting === "AREA" || def.targeting === "HEX") {
      for (const c of list) put(c.target, c);
    } else if (def.targeting === "SINGLE_ALLY" || def.targeting === "SINGLE_ENEMY"
               || def.targeting === "SINGLE_ANY") {
      for (const c of list) for (const h of bodyOf(state, c.target)) put(h, c);
    }
    // SELF / ALL / ALL_ALLIES / ALL_ENEMIES / NONE carry target -1 and are cast
    // straight from the book, so they never arm and never reach here.
    return { def, byHex, hexes };
  }, [aiming, casts, content, state, version]);

  // An armed spell survives until the state moves - the cast itself, or the
  // enemy's reply, both of which make the candidate list stale.
  useEffect(() => { setAiming(null); setCreatureSpell(null); setCastMenu(false); }, [state, version]);

  const selected = useMemo(() => {
    if (!placing) return -1;
    if (picked >= 0) {
      for (const c of legal) {
        if (c.type === "BATTLE_TACTICS_MOVE" && c.stack === picked) return picked;
      }
    }
    for (const c of legal) {
      if (c.type === "BATTLE_TACTICS_MOVE") return c.stack;
    }
    return -1;
  }, [placing, picked, legal]);

  const reachable = useMemo(() => {
    if (aim !== null) return aim.hexes;
    if (placing) {
      if (selected < 0) return EMPTY;
      const out: number[] = [];
      for (const c of legal) {
        if (c.type === "BATTLE_TACTICS_MOVE" && c.stack === selected) out.push(c.hex);
      }
      return out;
    }
    return mine ? reachOf(state, slot) : EMPTY;
  }, [aim, placing, selected, legal, state, version, slot, mine]);

  const attackFrom = useMemo((): readonly number[] => {
    if (!mine || aim !== null || hover.stack === null || hover.stack === slot) return EMPTY;
    if (state.stacks.side[hover.stack] === state.stacks.side[slot]) return EMPTY;
    const reach = computeReach(state, slot, newOccupancy(state));
    const out: number[] = [];
    const n = attackPositions(state, slot, hover.stack, reach, out);
    return out.slice(0, n);
  }, [state, version, slot, mine, aim, hover.stack]);

  // No em dashes and no turn prompt. A critic blind-named "Round 1 - Your move"
  // with a typographic em dash, centred in the message box, as one of the two
  // fastest tells on this screen: the classic's recess is a commentary in sentences
  // ("The Ballista does 4 damage. / Next round begins.",
  // a reference screenshot) and it never states whose turn it is,
  // because the board does - the acting stack wears a yellow outline
  // (`StackLayer.setActive`) and stands first in the queue.
  const acting = placing
    ? "Tactics — place troops, Done"
    : state.tacticsPhase
      ? "The enemy is arranging their army."
      : mine ? "Your move." : side === null ? "Watching." : "The enemy is moving.";

  const mana = side === null ? 0 : state.heroes[side].mana;
  const showBook = mine && spellChoices.length > 0;

  /**
   * Every batch of kernel events is narrated exactly once.
   *
   * `props.events` is a fresh array per batch, so its identity IS the batch
   * identity; a re-render that brings no new events - a hover, a resize - must
   * leave the strip alone, which is why nothing else is in the dependency list.
   */
  useEffect(() => {
    if (props.events.length === 0) return;
    const lines = narrateBattleEvents(state, content, props.events, {
      commanders: [props.commanders?.[0]?.name ?? null, props.commanders?.[1]?.name ?? null],
    });
    if (lines.length === 0) return;
    setNarration((prev) => narrationWindow(prev, lines));
  }, [props.events]);

  /**
   * What the crimson recess reads.
   *
   * The narration wins whenever there is any, because that is what the recess
   * is for in the classic - a commentary, not a status line. The round line is what
   * stands there before the first blow lands, and an armed spell overrides
   * both, exactly as the original replaces the log with "Attack Infernal
   * Cave Crawlers" while the cursor is over a target.
   */
  const status = useMemo(() => {
    if (aim !== null) return `Cast ${aim.def.name} on which target?`;
    // The armed blow reads the same way an armed spell does, for the same
    // reason: while something is waiting for a click the recess has to say so.
    if (striking) return "Strike which target?";
    if (narration.length > 0) return narration.join("\n");
    if (state.tacticsPhase) return acting;
    // The classic's own opening line, in the classic's own words.
    return mine ? `Round ${String(state.round)} begins.` : acting;
  }, [aim, striking, narration, state.tacticsPhase, state.round, acting]);

  // Which console buttons the kernel would accept. The renderer paints them and
  // reports the press; it never decides whether a press is legal (I10).
  // During the tactics phase the kernel takes placements on the board and the
  // one-shot that ends it, so every turn stone goes dark and the defend stone
  // doubles as DONE (`consoleIntent` maps it to `tacticsDone`). Watching the
  // enemy place leaves no stone lit at all.
  const enabled = useMemo<readonly BattleAction[]>(() => {
    if (state.tacticsPhase) return placing && side !== null ? ["defend"] : [];
    const out: BattleAction[] = [];
    if (mine) out.push("wait", "defend");
    if (side !== null) out.push("retreat", "surrender");
    // The three added with the hero's blow, the faction racial and creature
    // activated abilities. Every one is enabled off the kernel's own list
    // rather than off a rule mirrored here: a racial that has been spent, a
    // cooldown that has not run out and a hero who already cast this round all
    // stop appearing without this file knowing why.
    if (strikeTargets.length > 0) out.push("strike");
    if (legal.some((c) => c.type === "BATTLE_RACIAL")) out.push("racial");
    if (legal.some((c) => c.type === "BATTLE_ACTIVATE")) out.push("activate");
    // Enabled only because `consoleIntent` gives "spellbook" a meaning of its
    // own. Before it did, the handler ended in a ternary between RETREAT and
    // SURRENDER, and this one line would have made the spellbook button
    // surrender the army - see `commands.ts`.
    if (showBook) out.push("spellbook");
    return out;
  }, [mine, side, showBook, legal, strikeTargets, placing, state.tacticsPhase]);

  const ui = useMemo<UiState>(() => ({
    activeStack: placing ? selected : slot,
    reachable,
    attackFrom,
    hoverHex: hover.hex,
    hoverStack: hover.stack,
    inspectStack: inspect,
    pointer: { x: hover.x, y: hover.y },
    // The renderer paints the movement shade only when asked (`UiState.showReach`
    // / `showGrid`), and nothing ever asked: the flag had a reader in `ui.ts`
    // and no writer here, so no battle ever showed where a stack could go.
    // Asked while the pointer is on the board, which is the moment the
    // question "where can I go" is being put - see the note in `ui.ts`.
    // Also asked while placing, so the tactics band's legal placements stay
    // lit as the selection moves: `reachable` is the kernel's own
    // BATTLE_TACTICS_MOVE list for the picked stack, already inside
    // `inTacticsBand`, with no console-side filtering.
    // Hover is the desktop question "where can I go", asked by putting the
    // pointer on the board. A touch screen has no hover at all, so on a phone
    // the shade only ever appeared after a tap - and a tap on the board is a
    // MOVE, so the only way to see the range was to commit to using it. The
    // active stack's range is simply always lit there.
    showReach: hover.hex !== null || placing || (phone.phone && mine),
    status,
    enabled,
    // Spread rather than assigned: `exactOptionalPropertyTypes` makes an
    // explicit `undefined` a different thing from an absent key, and an absent
    // key is what tells the console to fall back to its faction stand-in.
    ...(props.commanders === undefined ? {} : { commanders: props.commanders }),
  }), [placing, selected, slot, reachable, attackFrom, hover, inspect, status, enabled,
    props.commanders, phone.phone, mine]);

  // ---- mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const presenter = new BattlePresenter(atlas, content, {
      seed: 20260823,
      compactChrome: phone.phone,
      // A lost GPU context is silent: the mount already succeeded, so the
      // `catch` below never fires, and Pixi keeps ticking against a dead
      // context while the canvas stops painting. Reported here because the
      // symptom - a black battlefield under a console that still works, with
      // auto-battle still resolving fights - is otherwise unattributable.
      onContextLost: (lost: boolean) => {
        app.setError(lost
          ? "the battlefield's graphics context was lost - the phone's GPU "
            + "reclaimed it. Auto-battle still works; reopen the battle to retry."
          : "");
      },
    });
    builtRef.current = presenter;
    let live = true;
    const mounted = presenter.mount(canvas).then(() => {
      if (!live) { presenter.dispose(); return; }
      localRef.current = presenter;
      if (props.presenterRef !== undefined) props.presenterRef.current = presenter;
      presenter.setAnimationsEnabled(props.animations);
      presenter.update({ state, events: [], version: -1, ui });
      // The screen has to render again now that there is something to talk to:
      // the size, frame and pointer effects all ran on the mounting render,
      // found a null presenter and did nothing. Without this the board draws
      // once and never hears a click - which is the whole of "units will not
      // move in battle".
      setMountedAt((n) => n + 1);
    });
    mounted.catch((e: unknown) => {
      app.setError(`battle renderer failed to mount: ${e instanceof Error ? e.message : String(e)}`);
    });
    const drop = app.addSettler("battle", async () => {
      await mounted.catch(() => undefined);
      await localRef.current?.settle();
    });

    return () => {
      live = false;
      drop();
      // Whoever is awaiting `animate()` has to be let go before the canvas
      // under the animator disappears. The battle ends inside the same command
      // that lands the last blow, so the host is normally mid-await on the
      // death animation at exactly this moment; `skip` applies the plan's
      // terminal states and resolves that promise, and without it the turn loop
      // would wait for a ticker that is about to be destroyed.
      presenter.skip();
      localRef.current = null;
      if (builtRef.current === presenter) builtRef.current = null;
      if (props.presenterRef !== undefined && props.presenterRef.current === presenter) {
        props.presenterRef.current = null;
      }
      presenter.dispose();
    };
    // The presenter is built once per battle; state arrives through `update`.
      }, [atlas, content]);

  // ---- size
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const apply = (): void => {
      const w = Math.max(1, host.clientWidth);
      const h = Math.max(1, host.clientHeight);
      localRef.current?.setCompactChrome(phone.phone);
      localRef.current?.resize(w, h);
      setStage((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(host);
    return () => { ro.disconnect(); };
  }, [mountedAt, phone.phone]);

  // ---- frames
  useEffect(() => {
    localRef.current?.setAnimationsEnabled(props.animations);
  }, [props.animations, mountedAt]);

  useEffect(() => {
    localRef.current?.update({ state, events: props.events, version, ui });
  }, [state, props.events, version, ui, mountedAt]);

  // ---- pointer
  //
  // Attached once per mount. Everything that changes between renders is read
  // from `live` at the moment of the click, so the handlers never go stale and
  // the listeners never churn.
  const live = useRef({
    state, slot, mine, placing, selected, side, content, legal, aim, creatureAim,
    striking, strikeTargets, onCommand: props.onCommand,
  });
  live.current = {
    state, slot, mine, placing, selected, side, content, legal, aim, creatureAim,
    striking, strikeTargets, onCommand: props.onCommand,
  };

  const fireConsole = (action: BattleAction): void => {
    const l = live.current;
    // During the viewer's own placement the defend stone is DONE; every other
    // stone is dead (`consoleIntent` maps only that one). No aiming or book
    // state is touched - there is none to touch while placing.
    const intent = consoleIntent(action, {
      slot: l.slot, mine: l.mine, side: l.side, tactics: l.placing,
    });
    if (intent.kind === "spellbook") {
      setStriking(false);
      if (l.aim !== null) { setAiming(null); return; }
      setBookOpen((o) => !o);
      return;
    }
    if (intent.kind === "aimStrike") {
      setAiming(null);
      setBookOpen(false);
      setStriking((armed) => !armed);
      return;
    }
    setAiming(null);
    setStriking(false);
    setBookOpen(false);
    if (intent.kind === "command") l.onCommand(intent.command);
  };

  useEffect(() => {
    const presenter = builtRef.current;
    if (presenter === null) return;
    // The presenter reports ARENA coordinates, which is what the painted panel
    // wants and what a DOM panel cannot use. The viewport position of the press
    // is only on the raw event, so it is recorded here and read when a card
    // opens.
    const canvas = canvasRef.current;
    const recordPress = (e: PointerEvent): void => {
      pressRef.current = { x: e.clientX, y: e.clientY };
    };
    canvas?.addEventListener("pointerdown", recordPress);
    const detach = presenter.attachPointer({
      onHover: (hex: HexCoord | null, point) => {
        const l = live.current;
        const h = hex === null ? null : (hex as unknown as number);
        const st = h === null ? null : occupantAt(l.state, h);
        setHover({ hex: h, stack: st, x: point.x as unknown as number, y: point.y as unknown as number });
        setInspect((cur) => (cur === st ? cur : null));
      },
      onSelect: (hex: HexCoord | null, point, button) => {
        const l = live.current;
        // Right-click is "never mind": it skips the animation and disarms the
        // spellbook, which is what a classic player expects of it.
        if (button !== 0) {
          setAiming(null); setStriking(false); setCreatureSpell(null);
          if (button === 2 && hex !== null) setInspect(occupantAt(l.state, hex as unknown as number));
          return;
        }
        if (hex === null) return;
        const h = hex as unknown as number;

        // Info mode consumes the press. While it is on the board is a reference
        // sheet and not a movement surface: a tap that both explained a stack
        // and marched another one into it would be unusable on a phone, where
        // the tap IS the only pointer.
        if (infoModeRef.current) {
          const occupant = occupantAt(l.state, h);
          const { x, y } = pressRef.current;
          setInfoCard({
            card: occupant === null
              ? battleHexCard(l.state, props.content, h)
              : battleStackCard(l.state, props.content, occupant),
            x, y,
          });
          return;
        }

        if (l.placing) {
          const hit = tacticsClick(h, occupantAt(l.state, h), l.selected, l.legal);
          if (hit.kind === "select") setPicked(hit.stack);
          else if (hit.kind === "command") l.onCommand(hit.command);
          return;
        }

        if (!l.mine) return;

        // An armed hero blow takes the click before anything else does, the
        // way an armed spell does: while it is armed the board is a target
        // picker and not a movement surface.
        if (l.striking) {
          const target = occupantAt(l.state, h);
          const hit = target === null
            ? undefined
            : l.strikeTargets.find((c) => c.target === target);
          if (hit !== undefined) l.onCommand(hit);
          setStriking(false);
          return;
        }

        // The active stack's own spell, armed from the ability strip. Read
        // before the hero's aim for no deep reason - the two are never armed
        // at once - but consistently before the board's movement reading,
        // which is the part that matters.
        if (l.creatureAim !== null) {
          const cast = l.creatureAim.byHex.get(h);
          if (cast !== undefined) l.onCommand(cast);
          setCreatureSpell(null);
          return;
        }

        if (l.aim !== null) {
          const cast = l.aim.byHex.get(h);
          if (cast !== undefined) l.onCommand(cast);
          setAiming(null);
          return;
        }

        const target = occupantAt(l.state, h);
        if (target !== null && l.state.stacks.side[target] !== l.state.stacks.side[l.slot]) {
          const shoot = l.legal.find((c) => c.type === "BATTLE_SHOOT" && c.target === target);
          if (shoot !== undefined) { l.onCommand(shoot); return; }
          const froms = l.legal
            .filter((c): c is Extract<BattleCommand, { type: "BATTLE_ATTACK" }> =>
              c.type === "BATTLE_ATTACK" && c.target === target)
            .map((c) => c.hex);
          if (froms.length === 0) return;
          const pick = attackApproach(asHex(h), point, froms);
          const from = pick === null ? froms[0]! : (pick.hex as unknown as number);
          l.onCommand({ type: "BATTLE_ATTACK", stack: l.slot, hex: from, target });
          return;
        }

        const move = l.legal.find((c) => c.type === "BATTLE_MOVE" && c.hex === h);
        if (move !== undefined) l.onCommand(move);
      },
      /**
       * A console button was pressed.
       *
       * What the press MEANS is `consoleIntent`, which is pure and tested; this
       * only performs it. The two used to be one chain of `if`s ending in a
       * ternary between RETREAT and SURRENDER, so every action without a branch
       * of its own surrendered the army - see the note in `commands.ts`.
       */
      onCommand: (action: BattleAction) => { fireConsole(action); },
      onSkip: () => { localRef.current?.skip(); },
    });
    return () => {
      canvas?.removeEventListener("pointerdown", recordPress);
      detach?.();
    };
  }, [mountedAt]);

  /**
   * Where the console's own spellbook button is, in CSS pixels within the stage.
   *
   * The panel that lists the spells hangs off the button that opened it, so the
   * geometry has to be the renderer's and not a guess: `consoleLayout` is pure
   * and exported for exactly this, and the arena-to-CSS transform is the cover
   * fit `arenaScale` describes. Getting this from a fixed `left: 12px` is what
   * put the old chip on top of the attacking commander's face.
   */
  const bookAnchor = useMemo(() => {
    if (stage.w <= 0 || stage.h <= 0) return null;
    if (phone.phone) {
      // The painted spellbook button is hidden; hang the list off the HTML bar.
      return {
        right: 8,
        bottom: 8,
        maxHeight: Math.max(120, Math.round(stage.h - 24)),
      };
    }
    const s = arenaScale(stage.w, stage.h);
    const view = arenaViewRect(stage.w, stage.h);
    const i = BATTLE_ACTIONS.indexOf("spellbook");
    const r = consoleLayout(view).buttons[i];
    if (r === undefined) return null;
    const ox = Math.round((stage.w - ARENA_W * s) / 2);
    return {
      // Right-aligned on the button's right edge, rising off the console band.
      right: Math.max(8, Math.round(stage.w - (ox + (r.x + r.w) * s))),
      bottom: Math.round(CONSOLE_H * s) + 6,
      maxHeight: Math.max(120, Math.round(stage.h - CONSOLE_H * s - 24)),
    };
  }, [stage, phone.phone]);

  return (
    <div className={phone.phone ? "hm-battle hm-battle--phone" : "hm-battle"}>
      <div className="hm-battle__stage" ref={hostRef}>
        <canvas ref={canvasRef} />
        {/* Quick Combat.
            A DOM chip over the canvas, in the sanctioned arrangement (D8) and
            in the same kit as the ability chips beside it, because the console
            below is a fixed four-and-four of stone buttons with the narration
            strip centred between them - the layout note in `render/battle/ui.ts`
            is explicit that the two groups must stay the same width, so a ninth
            stone would move the strip off the arena's axis.

            Offered only to a player who is actually in this fight (`side`), and
            only while it is still running. It reads as a toggle rather than a
            one-shot: the classic lets a player take the reins back, and a fight the
            computer is losing is exactly when he wants to. */}
        {/* Info mode, beside Quick Combat and for the same reason it is a DOM
            chip rather than a ninth stone (see the note above).

            Unlike every button on the console this one is NOT gated on whose
            turn it is, or on the viewer commanding a side: reading an enemy
            stack's numbers while the computer thinks, or while spectating, is
            exactly when a player wants them. Right-click still opens the
            painted panel on a desktop; this is how a phone, which has no
            second button, reaches the same thing - and it carries far more. */}
        <div style={S.auto}>
          <button
            type="button"
            style={infoMode ? { ...S.ability, ...S.abilityOn } : S.ability}
            aria-pressed={infoMode}
            title={infoMode
              ? "Info mode is on: press a unit or a hex to read it. Press again to fight."
              : "Info: press a unit or a hex to see every number behind it"}
            onClick={() => {
              setInfoMode((on) => !on);
              setInfoCard(null);
            }}
          >
            {infoMode ? "Info ON" : "Info"}
          </button>
          {side !== null ? (
            <button
              type="button"
              style={autoBattle ? { ...S.ability, ...S.abilityOn } : S.ability}
              aria-pressed={autoBattle}
              title={autoBattle
                ? "The computer is fighting this battle. Click to take command back."
                : "Let the computer finish this battle (A)"}
              onClick={() => { app.setAutoBattle(!autoBattle); }}
            >
              {autoBattle ? "Auto ON" : "Auto"}
            </button>
          ) : null}
        </div>

        {placing ? (
          <div style={S.abilities}>
            <button
              type="button"
              style={S.ability}
              title="End the tactics phase and begin the battle"
              onClick={() => {
                if (side === null) return;
                props.onCommand(tacticsDone(side));
              }}
            >
              Done
            </button>
          </div>
        ) : abilities.length > 0 || creatureChoices.length > 0 ? (
          <div style={S.abilities}>
            {abilities.map((cmd) => {
              const label = ABILITY_LABEL[cmd.type];
              return (
                <button
                  key={cmd.type}
                  type="button"
                  style={S.ability}
                  title={label?.hint}
                  onClick={() => {
                    setAiming(null);
                    setCreatureSpell(null);
                    props.onCommand(cmd);
                  }}
                >
                  {label?.name ?? cmd.type}
                </button>
              );
            })}
            {/* The stack's own spells. These sit beside the activated abilities
                rather than on the console, because the console is a fixed set
                of eight stones in the renderer and a Stormcaller knows ten
                spells. Unlike the abilities above, a spell needs a target - so
                the press ARMS and the next click on the board casts, which is
                the gesture the hero's spellbook already uses. */}
            {/* Two spells lie flat; more fold into a menu. A Stormcaller
                knows TEN, and ten stones down the right edge was a wall of
                text standing on the battlefield - measured at 1600x900, and a
                phone has less room still. The threshold is where a column
                stops being a glance and starts being a list. */}
            {creatureChoices.length > 0 && creatureChoices.length <= FLAT_SPELLS
              ? creatureChoices.map((def) => (
                <SpellStone
                  key={def.id as string}
                  def={def}
                  armed={creatureSpell === def.id}
                  onArm={() => {
                    setAiming(null);
                    setStriking(false);
                    setCreatureSpell(creatureSpell === def.id ? null : def.id);
                  }}
                />
              ))
              : null}
            {creatureChoices.length > FLAT_SPELLS ? (
              <>
                <button
                  type="button"
                  style={creatureSpell === null ? S.ability : { ...S.ability, ...S.abilityArmed }}
                  title={creatureSpell === null
                    ? `${String(creatureChoices.length)} spells this stack may cast`
                    : "Click a target, or right-click to put the spell away"}
                  onClick={() => {
                    setAiming(null);
                    setStriking(false);
                    if (creatureSpell !== null) { setCreatureSpell(null); return; }
                    setCastMenu((o) => !o);
                  }}
                >
                  {creatureSpell === null
                    ? `Cast ▾ (${String(creatureChoices.length)})`
                    : `▸ ${content.spells.get(creatureSpell)?.name ?? "Cast"}`}
                </button>
                {castMenu && creatureSpell === null ? (
                  <div style={S.castMenu}>
                    {creatureChoices.map((def) => (
                      <SpellStone
                        key={def.id as string}
                        def={def}
                        armed={false}
                        onArm={() => {
                          setAiming(null);
                          setStriking(false);
                          setCreatureSpell(def.id);
                          setCastMenu(false);
                        }}
                      />
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        {showBook && bookOpen && aim === null && bookAnchor !== null ? (
          <div
            style={{ ...S.book, right: bookAnchor.right, bottom: bookAnchor.bottom }}
            aria-label="Spellbook"
          >
            <div style={S.bookHead}>
              <span>Spellbook</span>
              <span style={S.cost}>{String(mana)} mana</span>
            </div>
            <ul style={{ ...S.list, maxHeight: bookAnchor.maxHeight }}>
              {spellChoices.map(({ def, mana: cost }) => (
                <li key={def.id as string}>
                  <button
                    type="button"
                    style={S.spell}
                    onClick={() => {
                      setBookOpen(false);
                      // A spell with nothing to point at is cast the moment it
                      // is chosen; everything else arms and waits for a hex.
                      const immediate = casts.find((c) => c.spell === def.id && c.target < 0);
                      if (immediate !== undefined) { props.onCommand(immediate); return; }
                      setAiming(def.id);
                    }}
                  >
                    <span>{def.name}</span>
                    <span style={S.cost}>{String(cost)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* The same words the crimson recess is painting, for a reader who
            cannot see a canvas - and, incidentally, the only handle a harness
            has on text that lives in a Pixi scene graph. Off-screen rather than
            `display:none`, which assistive technology skips. */}
        <div role="status" aria-live="polite" data-battle-status={status} style={S.srOnly}>
          {status}
        </div>
      </div>
      {phone.phone ? (
        <div className="hm-battle__touchbar" role="toolbar" aria-label="Battle commands">
          {enabled.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => { fireConsole(action); }}
            >
              {placing && action === "defend" ? "Done" : TOUCH_CONSOLE_LABEL[action]}
            </button>
          ))}
        </div>
      ) : null}

      {/* Outside the stage div because it is `position: fixed` and, on a phone,
          a bottom sheet across the whole viewport rather than a card over the
          board. It stays up until it is dismissed or another hex is pressed:
          a card that vanished on pointer-up would be unreadable on the device
          that needs it most. */}
      {infoCard !== null ? (
        <BattleInfoPanel
          card={infoCard.card}
          x={infoCard.x}
          y={infoCard.y}
          phone={phone.phone}
          onDismiss={() => { setInfoCard(null); }}
        />
      ) : null}
    </div>
  );
}

/** How many of a stack's spells lie flat before they fold into a menu. */
const FLAT_SPELLS = 2;

/** One spell the active stack may cast: press to arm, press again to disarm. */
function SpellStone(props: {
  readonly def: SpellDef;
  readonly armed: boolean;
  readonly onArm: () => void;
}): JSX.Element {
  const { def, armed } = props;
  return (
    <button
      type="button"
      style={armed ? { ...S.ability, ...S.abilityArmed } : S.ability}
      title={armed
        ? `Click a target to cast ${def.name}, or right-click to put it away`
        : `${def.name}: ${def.description}`}
      onClick={props.onArm}
    >
      {armed ? `▸ ${def.name}` : def.name}
    </button>
  );
}

/**
 * The creature powers this screen knows how to name.
 *
 * A command the kernel enumerates but this table does not name is simply not
 * offered, which is the safe direction: a new ability appears as a missing
 * button rather than as an unlabelled one, and `reachability.spec.ts` fails
 * until somebody names it. Every entry takes the active stack and nothing else,
 * which is why the strip needs no aiming step.
 */
const ABILITY_LABEL: Readonly<Record<string, { readonly name: string; readonly hint: string }>> = {
  // Quoted keys, not bare ones. `reachability.spec.ts` proves a command is
  // reachable by looking for the literal in quotes, which is what a real
  // construction or comparison looks like - a bare object key would satisfy the
  // compiler and leave the command as unreachable as it was.
  "BATTLE_HEAT_STROKE": {
    name: "Heat Stroke",
    hint: "Vent the boiler into the hexes ahead. It burns friend and foe alike "
      + "and draws no retaliation, and venting into empty air is legal - it "
      + "simply spends the turn.",
  },
  "BATTLE_MEDITATE": {
    name: "Meditate",
    hint: "Draw on the ley lines instead of acting. Once a battle.",
  },
};

/* The spellbook is OPENED by the console's own stone button and only listed
   here: a book is a list and the console's buttons are a fixed set of glyphs in
   the renderer. It used to be a chip pinned at `left: 12, top: 12` carrying its
   own "SPELLBOOK - 40 MANA" label, and that chip sat squarely on the attacking
   commander's portrait and name at both shipped viewports. The list now hangs
   off the button that opened it, measured with the renderer's own
   `consoleLayout`, so it cannot drift onto anything the console draws.

   It is a DOM panel over the canvas, which is the sanctioned arrangement (D8) -
   the canvas owns the pointer below the panel layer, the panel owns it within
   its own bounds, and nothing calls stopPropagation. Styled here rather than in
   global.css so the battle screen carries its own chrome. */
const S: Readonly<Record<string, CSSProperties>> = {
  /** Top LEFT: the ability chips own the top right, and a fight with a racial
   *  and an activated ability would otherwise push this one down the column
   *  and move it between turns. */
  auto: {
    position: "absolute", left: 12, top: 12, zIndex: 2,
    font: "600 12px/1.2 ui-sans-serif, system-ui, sans-serif", letterSpacing: "0.06em",
  },
  abilityOn: {
    color: "#1d1408",
    background: "linear-gradient(#e6c877, #b98f36)",
    borderColor: "#f4dfa6",
  },
  castMenu: {
    display: "flex", flexDirection: "column", alignItems: "stretch", gap: 4,
    maxHeight: "46vh", overflowY: "auto",
    padding: 6, borderRadius: 3,
    background: "rgba(20, 14, 8, 0.96)", border: "1px solid #8d6a2f",
  },
  /** The armed spell, told apart from its unarmed siblings at a glance. */
  abilityArmed: {
    background: "#3c2f14",
    borderColor: "#d7b55a",
    color: "#ffe9a8",
  },
  abilities: {
    position: "absolute", right: 12, top: 12, zIndex: 2,
    display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6,
    font: "600 12px/1.2 ui-sans-serif, system-ui, sans-serif", letterSpacing: "0.06em",
  },
  ability: {
    padding: "6px 12px", cursor: "pointer", color: "#f2e4c1",
    background: "linear-gradient(#5a3f22, #33220f)",
    border: "1px solid #d9b25a", borderRadius: 3, font: "inherit",
    textTransform: "uppercase", letterSpacing: "0.08em",
  },
  book: {
    position: "absolute", zIndex: 2,
    display: "flex", flexDirection: "column", alignItems: "stretch",
    minWidth: 210, background: "rgba(20, 14, 8, 0.96)",
    border: "2px solid #7a5c2e", borderRadius: 3,
    boxShadow: "0 6px 18px rgba(0,0,0,0.65)",
    font: "600 12px/1.2 ui-sans-serif, system-ui, sans-serif", letterSpacing: "0.06em",
  },
  bookHead: {
    display: "flex", justifyContent: "space-between", gap: 14,
    padding: "6px 10px", color: "#f0e0b6",
    background: "linear-gradient(#4a3925, #2a1e12)",
    borderBottom: "1px solid #7a5c2e",
    textTransform: "uppercase", letterSpacing: "0.08em",
  },
  list: {
    listStyle: "none", margin: 0, padding: 4, overflowY: "auto",
    background: "transparent", border: 0,
  },
  /** Reachable by a screen reader, invisible to the screenshot harness. */
  srOnly: {
    position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
    overflow: "hidden", clip: "rect(0 0 0 0)", clipPath: "inset(50%)",
    whiteSpace: "nowrap", border: 0,
  },
  spell: {
    display: "flex", justifyContent: "space-between", gap: 14, width: "100%",
    padding: "5px 8px", cursor: "pointer", color: "#e8d7ad",
    background: "transparent", border: 0, font: "inherit", textAlign: "left",
  },
  cost: { color: "#79b6e8" },
};
